import { createAsyncThunk } from '@reduxjs/toolkit';

import { GenericAjaxType } from 'redux/store';
import { postRequest, storeUserData, deleteUserData } from 'services';
import {
  initializeKeyPair,
  getPublicKeyPEM,
  decryptAESKey,
  decryptAESIV,
  encryptCredentials,
} from 'services/auth-encryption';
import { clearThumbnailToken } from 'components/shared/signed-thumbnail';
import { getErrorMsg, onAjaxEnhancer } from '../store.util';
import type { LoginData, Session, SessionError } from './session.type';

const TYPE_PREFIX = '@drivex/session';

const login = createAsyncThunk<Session, LoginData, { rejectValue: SessionError }>(`${TYPE_PREFIX}/login`, async (payload, thunkApi) => {
  try {
    // Step 0: Generate fresh RSA key pair for this login attempt
    const keyPair = await initializeKeyPair();

    // Step 1: Get public key in PEM format
    const publicKey = await getPublicKeyPEM(keyPair);

    // Step 2: Exchange keys with server (handshake)
    const handshakeResponse = await postRequest('/session/handshake', { publicKey });

    const { sessionId, encryptedAesKey, encryptedAesIV } = handshakeResponse.data;

    // Step 3: Decrypt AES key and IV using RSA private key
    const aesKey = await decryptAESKey(keyPair, encryptedAesKey);
    const aesIV = await decryptAESIV(keyPair, encryptedAesIV);

    // Step 4: Encrypt credentials using AES
    const encryptedPayload = await encryptCredentials(
      {
        Email: payload.loginData.email,
        Password: payload.loginData.password,
      },
      aesKey,
      aesIV
    );

    // Step 5: Send encrypted login request
    const response = await postRequest('/session/secure', {
      sessionId,
      encryptedPayload,
    });

    // MFA is enabled for this user and a code was (or already had been) sent to their e-mail.
    // Hold off on persisting the session until the code has been verified.
    if (!response.data?.mfaPending) {
      storeUserData(response.data);
    }

    onAjaxEnhancer(payload, ['onSuccess', 'onFinish']);
    return response.data;
  } catch (error: any) {
    onAjaxEnhancer(payload, ['onError', 'onFinish']);
    return thunkApi.rejectWithValue({
      message: getErrorMsg(error.response),
    });
  }
});

const logout = createAsyncThunk<void, GenericAjaxType, { rejectValue: SessionError }>(`${TYPE_PREFIX}/logout`, async (payload, thunkApi) => {
  deleteUserData();
  clearThumbnailToken();
  try {
    const response = await postRequest('/session/logout', {});
    onAjaxEnhancer(payload, ['onSuccess', 'onFinish']);
    return response.data as void;
  } catch (error: any) {
    onAjaxEnhancer(payload, ['onError', 'onFinish']);
    return thunkApi.rejectWithValue({
      message: getErrorMsg(error.response),
    });
  }
});

export { login, logout };
